import { type FC } from 'react'
import styled from '@emotion/styled'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import HeroRecipeBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import { SignupButtonText } from '@segment/matcha/recipes/SignupButton'

import DemoForm from '@segment/templates/Forms/DemoForm'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'

const copy = {
  subtitle: `Streamline data management with Twilio Segment`,
  heading: `Collect and manage data from everywhere`,
  description: `Optimize data collection, compliance, and activation in real-time using Segment’s flexible platform, ensuring quality data and seamless integration with your data warehouse and technology stack.`,
}

const afterContent = (
  <SignupButtonText size="medium" tag="a">
    Create a free account
    <ChevronRightIcon />
  </SignupButtonText>
)

const Hero: FC = () => {
  return (
    <Wrapper>
      <StyledHero variant="2Col">
        <HeroRecipe.Copy>
          <CopyText.Overline>{copy.subtitle}</CopyText.Overline>
          <CopyText.Heading variant="homeH2" tag="h1">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="homePExtraLarge" tag="p">
            {copy.description}
          </CopyText.Description>

          <HeroRecipe.Form>
            <DemoFormContainer data-hook="demoFormContainer">
              <DemoForm
                isProgressive={true}
                customFormIdentifier="site-use-case-data-management-demo"
                customTrackingType="site-use-case-data-management-demo"
                after={afterContent}
              />
            </DemoFormContainer>
          </HeroRecipe.Form>
        </HeroRecipe.Copy>
        <HeroRecipe.Contents>
          <picture>
            <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
            <HeroRecipe.Image
              width="1350"
              height="1034"
              src={heroPng2x}
              alt="Illustration: Customer Data Management Integration"
              loading="eager"
            />
          </picture>
        </HeroRecipe.Contents>
      </StyledHero>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background: ${({ theme }) => theme.palette.twilio.gray[100]};
`

const StyledHero = styled(HeroRecipeBase)`
  ${HeroRecipe.Wrapper} {
    gap: 50px;
  }
`

const DemoFormContainer = styled.div`
  margin-top: 50px;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin-top: 0;
  }
`

export default Hero
